import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const RELOAD_UNLOCK_SECONDS = 3;

export interface GlobalErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  details?: string | null;
  /** When true, only the reload action is offered (used for RPC rate limits). */
  requireReload?: boolean;
  onReload?: () => void;
}

const GlobalErrorDialog = ({
  open,
  onOpenChange,
  title = "Something went wrong",
  description = "An unexpected error occurred. You can dismiss this message and continue, or reload the page if the problem persists.",
  details,
  requireReload = false,
  onReload,
}: GlobalErrorDialogProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reloadSecondsLeft, setReloadSecondsLeft] = useState(RELOAD_UNLOCK_SECONDS);

  useEffect(() => {
    if (!open || !requireReload) {
      setReloadSecondsLeft(RELOAD_UNLOCK_SECONDS);
      return;
    }

    let secondsLeft = RELOAD_UNLOCK_SECONDS;
    setReloadSecondsLeft(secondsLeft);
    const intervalId = window.setInterval(() => {
      secondsLeft -= 1;
      setReloadSecondsLeft(Math.max(0, secondsLeft));
      if (secondsLeft <= 0) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [open, requireReload]);

  const reloadLocked = requireReload && reloadSecondsLeft > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Rate-limit errors must be cleared via reload — ignore dismiss / overlay close.
        if (!next && requireReload) return;
        if (!next) setDetailsOpen(false);
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="glass sm:max-w-lg z-[10000]"
        aria-describedby="global-error-description"
        hideCloseButton={requireReload}
        onPointerDownOutside={requireReload ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={requireReload ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/15">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription id="global-error-description">{description}</DialogDescription>
        </DialogHeader>

        {details ? (
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-between px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <span>Error details</span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 transition-transform duration-200",
                    detailsOpen && "rotate-180",
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-left text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
                {details}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          {onReload ? (
            <div className="flex items-center gap-3">
              {reloadLocked ? (
                <span
                  className="text-sm tabular-nums text-muted-foreground"
                  aria-live="polite"
                >
                  Available in {reloadSecondsLeft}s
                </span>
              ) : null}
              <Button
                type="button"
                variant={requireReload ? "default" : "outline"}
                disabled={reloadLocked}
                onClick={onReload}
              >
                Reload page
              </Button>
            </div>
          ) : null}
          {!requireReload ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Dismiss
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalErrorDialog;
